import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

function D20Icon({ className, style, rolledNumber, isRolling }) {
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="1.5">
      <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" />
      <line x1="50" y1="5" x2="50" y2="50" />
      <line x1="95" y1="25" x2="50" y2="50" />
      <line x1="95" y1="75" x2="50" y2="50" />
      <line x1="50" y1="95" x2="50" y2="50" />
      <line x1="5" y1="75" x2="50" y2="50" />
      <line x1="5" y1="25" x2="50" y2="50" />
      <circle cx="50" cy="52" r="18" fill="#050510" stroke="none" />
      <text 
        x="50" 
        y="59" 
        textAnchor="middle" 
        fill="#ffffff" 
        stroke="none" 
        fontSize="22" 
        fontFamily="Orbitron, monospace" 
        fontWeight="bold"
        className={isRolling ? 'animate-pulse' : ''}
      >
        {isRolling ? '?' : rolledNumber}
      </text>
    </svg>
  )
}

export default function D20WillpowerRoll() {
  const [rolledNumber, setRolledNumber] = useState(20)
  const [isRolling, setIsRolling] = useState(false)
  const [rollResult, setRollResult] = useState(null)

  const getOutcome = (num) => {
    if (num === 1) {
      return {
        title: 'FUMBLE! (Fallimento Critico)',
        color: '#ef4444',
        glow: 'rgba(239, 68, 68, 0.35)',
        badge: 'Status: Paralizzato',
        desc: 'Hai provato ad alzarti dal letto, ma le dita si sono mosse da sole toccando l\'icona di Instagram. Subisci 50 danni da nebbia mentale e rimani intrappolato nello scroll dei Reel per altri 45 minuti. Stamina azzerata, focus a pezzi.',
        stats: { XP: '-10', Stamina: '-30%', Focus: '-40%' }
      }
    } else if (num >= 2 && num <= 8) {
      return {
        title: 'FALLIMENTO SEMPLICE',
        color: '#ef4444',
        glow: 'rgba(239, 68, 68, 0.15)',
        badge: 'Debuff: Ancora 5 Minuti',
        desc: 'Tenti di lanciare la coperta, ma il debuff calore passivo ha la meglio. Spegni la sveglia con gli occhi chiusi e cadi in un sonno comatoso. Ti svegli in ritardo rimandando le prime sub-quest della giornata. Focus in standby.',
        stats: { XP: '+0', Stamina: '-15%', Focus: '-20%' }
      }
    } else if (num >= 9 && num <= 14) {
      return {
        title: 'SUCCESSO PARZIALE',
        color: '#facc15',
        glow: 'rgba(250, 204, 21, 0.2)',
        badge: 'Buff: Sonnolenza Attiva',
        desc: 'Roll di volontà superato per il rotto della cuffia. Ti trascini fuori dal letto bevendo il caffè a occhi semichiusi. Non apri subito i social, ma la tentazione di procrastinare è alta. Sei operativo al 50%.',
        stats: { XP: '+10', Stamina: '+10%', Focus: '+15%' }
      }
    } else if (num >= 15 && num <= 19) {
      return {
        title: 'SUCCESSO OPERATIVO',
        color: '#00d4ff',
        glow: 'rgba(0, 212, 255, 0.3)',
        badge: 'Buff: Mente Chiara',
        desc: 'Ottimo lancio! Ti alzi all\'istante, non tocchi lo smartphone per la prima mezz\'ora ed esegui 5 minuti di respirazione profonda e mobilità fisica. Il tuo sistema nervoso ringrazia. Sbloccato bonus focus per la prima quest lavorativa.',
        stats: { XP: '+30', Stamina: '+40%', Focus: '+50%' }
      }
    } else {
      return {
        title: 'SUCCESSO CRITICO! (Natural 20)',
        color: '#8b5cf6',
        glow: 'rgba(139, 92, 246, 0.45)',
        badge: 'Buff Divino: Pro Player Offline',
        desc: 'NATURAL 20! Esecuzione leggendaria. Sconfiggi all\'istante il drago del tasto Snooze. Ti alzi ridendo del sonno, sbrighi la mobilità, bevi acqua e affronti la tua quest reale più complessa prima delle 10:00. Livello di focus massimo sbloccato.',
        stats: { XP: '+100', Stamina: '+80%', Focus: 'MAX' }
      }
    }
  }

  const handleRoll = () => {
    if (isRolling) return
    setIsRolling(true)
    setRollResult(null)

    // Simulazione rotazione dado veloce
    let count = 0
    const interval = setInterval(() => {
      setRolledNumber(Math.floor(Math.random() * 20) + 1)
      count++
      if (count > 10) {
        clearInterval(interval)
        const finalNum = Math.floor(Math.random() * 20) + 1
        setRolledNumber(finalNum)
        setRollResult(getOutcome(finalNum))
        setIsRolling(false)
      }
    }, 85)
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-4 mb-10">
      <div 
        className="manga-panel rounded-2xl p-6 md:p-8 relative overflow-hidden transition-all duration-500"
        style={{
          borderColor: rollResult ? `${rollResult.color}45` : 'rgba(139,92,246,0.2)',
          boxShadow: rollResult 
            ? `0 8px 32px rgba(5,5,16,0.5), 0 0 30px ${rollResult.glow}` 
            : '0 8px 32px rgba(5,5,16,0.5)',
        }}
      >
        {/* Glow decorativo interno */}
        <div 
          className="absolute -inset-10 opacity-30 pointer-events-none transition-all duration-700" 
          style={{
            background: rollResult 
              ? `radial-gradient(circle at 50% 50%, ${rollResult.glow} 0%, transparent 60%)`
              : 'radial-gradient(circle at 50% 50%, rgba(139,92,246,0.06) 0%, transparent 60%)'
          }}
        />

        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          
          {/* Colonna Sinistra — Il Dado */}
          <div className="flex flex-col items-center justify-center shrink-0">
            <motion.div
              className="cursor-pointer text-nerd-cyan"
              onClick={handleRoll}
              whileHover={{ scale: isRolling ? 1 : 1.06, color: '#8b5cf6' }}
              whileTap={{ scale: isRolling ? 1 : 0.94 }}
              animate={isRolling ? {
                rotate: [0, 360, 720, 1080],
                y: [0, -18, 5, -8, 0],
                scale: [1, 1.15, 0.95, 1.05, 1],
              } : {}}
              transition={{
                duration: 0.9,
                ease: 'easeInOut'
              }}
              style={{
                width: '130px',
                height: '130px',
                color: rollResult ? rollResult.color : '#00d4ff',
                filter: rollResult ? `drop-shadow(0 0 12px ${rollResult.color}50)` : 'drop-shadow(0 0 8px rgba(0,212,255,0.25))'
              }}
            >
              <D20Icon rolledNumber={rolledNumber} isRolling={isRolling} className="w-full h-full" />
            </motion.div>
            
            <button
              onClick={handleRoll}
              disabled={isRolling}
              className="mt-4 px-4 py-1.5 rounded bg-nerd-dark border border-nerd-purple/30 hover:border-nerd-cyan text-[10px] font-display uppercase tracking-widest text-nerd-white hover:text-nerd-cyan transition-colors"
            >
              {isRolling ? 'Rolling...' : 'Tira Iniziativa 🎲'}
            </button>
          </div>

          {/* Colonna Destra — Informazioni */}
          <div className="flex-1 text-center md:text-left space-y-4">
            <div>
              <div className="flex items-center justify-center md:justify-start gap-2 mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-nerd-cyan animate-pulse" />
                <span className="text-[9px] text-nerd-cyan font-display uppercase tracking-[0.25em]">Quest Attiva</span>
              </div>
              <h3 className="font-display text-xl font-bold text-nerd-white">Tira per l'Iniziativa Quotidiana</h3>
            </div>

            <p className="text-nerd-gray text-xs md:text-sm leading-relaxed max-w-md font-body">
              Sei bloccato a letto la mattina con lo smartphone in mano. Qual è la tua mossa reale? Clicca sul dado per lanciare il test di Forza di Volontà ed affrontare la giornata!
            </p>

            <AnimatePresence mode="wait">
              {rollResult && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  className="p-4 rounded-xl bg-nerd-dark/50 border space-y-2 text-left"
                  style={{ borderColor: `${rollResult.color}25` }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h4 className="font-display text-xs font-bold uppercase tracking-wider" style={{ color: rollResult.color }}>
                      {rollResult.title}
                    </h4>
                    <span 
                      className="px-2 py-0.5 text-[8px] font-display rounded border font-semibold uppercase tracking-wider"
                      style={{ 
                        color: rollResult.color, 
                        borderColor: `${rollResult.color}35`, 
                        background: `${rollResult.color}10` 
                      }}
                    >
                      {rollResult.badge}
                    </span>
                  </div>

                  <p className="text-nerd-gray text-xs leading-relaxed font-body">
                    {rollResult.desc}
                  </p>

                  <div className="flex gap-4 pt-1.5 border-t border-nerd-purple/10">
                    {Object.entries(rollResult.stats).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-1">
                        <span className="text-[8px] text-nerd-gray uppercase font-display">{k}:</span>
                        <span className="text-[10px] font-display font-bold" style={{ color: rollResult.color }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="absolute top-2 left-2 w-3 h-3 border-l border-t border-nerd-purple/20" />
        <div className="absolute top-2 right-2 w-3 h-3 border-r border-t border-nerd-purple/20" />
        <div className="absolute bottom-2 left-2 w-3 h-3 border-l border-b border-nerd-purple/20" />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-nerd-purple/20" />
      </div>
    </div>
  )
}
